<template>
  <v-card class="ma-0 px-1" width="100%" flat>
    <v-app-bar flat class="my-0 mb-2 py-1 px-4" dense height="30">
      <v-toolbar-title> {{ $t("transfer") }} </v-toolbar-title>
      <v-radio-group v-model="type" class="mt-6" row>
        <v-radio
          :label="$t('fromDebtor')"
          value="receivable"
          :disabled="id ? true : false"
        />
        <v-radio
          :label="$t('fromCreditor')"
          value="payable"
          :disabled="id ? true : false"
        />
      </v-radio-group>
      <v-spacer />
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-if="id"
            v-bind="attrs"
            icon
            small
            class="float-left"
            v-on="on"
            @click="goTo(type)"
          >
            <v-icon color="teal" style="font-size: 22px"> mdi-plus-box-outline </v-icon>
          </v-btn>
        </template>
        <span>{{ $t(type) }}</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn v-bind="attrs" icon small v-on="on" @click="goTo('transferList')">
            <v-icon color="primary" style="font-size: 22px">
              mdi-format-list-numbered
            </v-icon>
          </v-btn>
        </template>
        <span>{{ $t("transferList") }}</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn v-bind="attrs" icon small v-on="on" @click="$router.go(-1)">
            <v-icon color="primary" style="font-size: 22px"> mdi-arrow-left </v-icon>
          </v-btn>
        </template>
        <span>{{ $t("back") }}</span>
      </v-tooltip>
    </v-app-bar>
    <v-progress-linear
      :active="!render"
      :indeterminate="!render"
      absolute
      bottom
      style="top: -4px"
      color="primary"
    />
    <v-form v-if="render" ref="form" v-model="valid" lazy-validation>
      <v-tabs>
        <v-tab style="height: 50px">
          {{ $t("public-information") }}
        </v-tab>
        <v-tab :disabled="!documentPermmission">
          {{ $t("documents") }}
        </v-tab>
        <v-tab-item>
          <v-container>
            <v-row>
              <v-col class="px-3 xs" cols="12" md="2">
                <autocomplete
                  v-model="payer"
                  :rules="[(v) => !!v || $t('is-required')]"
                  :title="$t('payer')"
                  type="detailTransfer"
                  :new-btn="true"
                  :value="payer"
                />
              </v-col>
              <v-col cols="12" md="2">
                <autocomplete
                  v-model="payerGiven"
                  :title="$t('payerGiven')"
                  type="given"
                />
              </v-col>
              <v-col cols="12" md="2">
                <money
                  v-model="amount"
                  :label="$t('amount')"
                  :dense="true"
                  :outlined="true"
                  :rules="[(v) => !!v || $t('is-required')]"
                  :solo="false"
                  :clearable="true"
                  value-when-is-empty=""
                  :options="options"
                  :value-options="{
                    max: bank ? Number(bank.remain.currency_remain) : 9999999999999999999,
                  }"
                  @SetValueMax="SetValueMaxPrice()"
                />
              </v-col>
              <v-col cols="12" md="2" sm="6">
                <template>
                  <v-text-field
                    id="from"
                    v-model="date"
                    :label="$t('date')"
                    :rules="[(v) => !!v || $t('is-required')]"
                    clear-icon="flaticon2-close-cross"
                    outlined
                    dense
                    clearable
                  />
                  <date-picker
                    v-model="date"
                    element="from"
                    :locale="$store.state.date_locale"
                    :format="$store.state.date_format"
                    auto-submit
                    :min="$store.state.startDate"
                    :max="$store.state.endDate"
                    :color="
                      this.$vuetify.theme.themes[
                        this.$vuetify.theme.isDark ? 'dark' : 'light'
                      ].primary
                    "
                    @close="setDate"
                  />
                </template>
              </v-col>
              <v-col cols="12" sm="2" md="2">
                <autocomplete
                  ref="refCurrency"
                  v-model="currency"
                  :rules="[(v) => !!v || $t('is-required')]"
                  :title="$t('currency')"
                  type="currency"
                  :value="currency"
                  :disabled="id ? false : false"
                  :init="defaultCurrency"
                  @change="changeCurrency"
                />
              </v-col>
              <v-col cols="12" sm="2" md="2">
                <money
                  v-model="currency_amount"
                  :label="$t('currency-rate')"
                  :dense="true"
                  :outlined="true"
                  :rules="[(v) => !!v || $t('is-required')]"
                  :solo="false"
                  :clearable="true"
                  value-when-is-empty=""
                  :options="options"
                />
              </v-col>
              <v-col v-if="currency" class="px-3 xs" cols="12" md="2">
                <autocomplete
                  v-model="receiver"
                  :rules="[(v) => !!v || $t('is-required')]"
                  :title="$t('receiver')"
                  type="detailTransfer"
                  :new-btn="true"
                  :value="receiver"
                />
              </v-col>
              <v-col cols="12" md="2">
                <autocomplete
                  v-model="receiverGiven"
                  :title="$t('receiverGiven')"
                  type="given"
                />
              </v-col>
              <v-col cols="12" md="2">
                <autocomplete
                  v-model="branch"
                  :title="$t('branch')"
                  type="branch"
                  :set-init="true"
                  :value="branch"
                />
              </v-col>
              <v-col cols="12" md="2">
                <tag v-model="tag" :solo="false" :input="tag" :height="25" />
              </v-col>
              <v-col cols="12" md="4">
                <description
                  ref="refDescription"
                  v-model="content"
                  :outlined="true"
                  type="sell"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-tab-item>
        <v-tab-item>
          <v-row class="mx-0 pt-2">
            <v-col cols="12" md="3" sm="3">
              <file-picker
                ref="refFilePicker"
                v-model="attachments"
                :label="$t('addDocuments')"
                :multiple="true"
                type-prop="transfer"
                :val="id && attachments ? attachments : null"
              />
            </v-col>
          </v-row>
        </v-tab-item>
      </v-tabs>
      <v-card-actions>
        <v-row class="ma-0">
          <v-col cols="6" class="px-1">
            <v-btn
              v-if="!id"
              color="primary"
              class="mr-0"
              :loading="isLoading"
              @click="create"
            >
              {{ $t("submit") }}
            </v-btn>
            <v-btn
              v-if="id"
              color="primary"
              class="mr-0"
              :loading="isLoading"
              @click="update"
            >
              {{ $t("submit") }}
            </v-btn>
          </v-col>
          <v-col cols="6" class="px-2"> </v-col>
        </v-row>
      </v-card-actions>
    </v-form>
    <v-snackbar
      v-model="snakbar"
      :color="snakbarColor ? snakbarColor : 'primary'"
      top
      style="z-index: 1000000000000 !important"
    >
      {{ snakbarText }}

      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snakbar = !snakbar">
          {{ $t("close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
// import Combobox from '../../components/general/combobox/Combobox'
import autocomplete from "../../components/general/patial/autocomplete";
import Description from "../../components/general/description/Description";
import General from "../../../../Mixins/general";
import filePicker from "../../components/general/filePicker/index";
export default {
  name: "Transfer",
  components: {
    // Combobox,
    Tag: () => import("../../components/general/Tag/tag"),
    autocomplete,
    filePicker,
    Description,
  },
  mixins: [General],
  props: ["typeForm", "accountProp"],
  data: () => ({
    valid: true,
    dateFormat: "YYYY/MM/DD",
    more: true,
    attachments: null,
    currency: null,
    defaultCurrency: null,
    accountGiven: null,
    bankGiven: null,
    receiver: null,
    payer: null,
    payerGiven: null,
    receiverGiven: null,
    content: null,
    id: null,
    extra: false,
    relations: null,
    snakbar: null,
    snakbarText: null,
    snakbarColor: null,
    sumRelations: null,
    currency_amount: 1,
    tag: null,
    remianBankColor: "blue",
    remianColor: "blue",
    customerId: null,
    bankRemain: null,
    taxStatus: null,
    taxStatusItems: null,
    clearing: null,
    snackbarShow: null,
    amount: 0,
    editPrice: 0,
    type: "receivable",
    NetPrice: 0,
    taxAmount: 0,
    tax: 9,
    discount: 0,
    taxFeilds: null,
    discountFeilds: null,
    account: null,
    options: {
      locale: "en-US",
      prefix: "",
      suffix: "  ",
      precision: 0,
    },
    optionsTax: {
      locale: "en-US",
      prefix: "",
      suffix: "  %  ",
      precision: 0,
    },
    detail: null,
    bank: null,
    remianLable: null,
    description: null,
    date: null,
    render: true,
    isLoading: false,
  }),
  computed: {
    sum() {
      if (this.taxStatus === "notAdd") {
        return (
          parseFloat(this.price) - parseFloat(this.discount) + parseFloat(this.editPrice)
        );
      } else {
        return (
          parseFloat(this.price) - parseFloat(this.discount) + parseFloat(this.taxAmount)
        );
      }
    },
  },
  watch: {
    defaultCurrency() {
      if (this.defaultCurrency) {
        this.$refs.refCurrency.setModel(this.defaultCurrency);
        this.currency_amount = this.defaultCurrency.amount;
      }
    },
    bank() {
      if (this.render) {
        this.bankRemainCheck();
      }
    },
    account() {
      this.remianLable = null;
      this.remianColor = "blue";
      if (this.account.remain && this.account.remain.remain > 0) {
        this.remianColor = "pink";
        this.remianLable = this.$t("debtor");
      }
      if (this.account.remain && this.account.remain.remain < 0) {
        this.remianColor = "green";
        this.remianLable = this.$t("creditor");
      }
      if (this.$refs.refRelations) {
        this.$refs.refRelations.initList = false;
        this.relations = null;
        this.$refs.refRelations.clear();
        this.$refs.refRelations.getItems();
      }
    },
    currency() {
      if (!this.id) {
        this.amount = 0;
        this.bankRemain = 0;
        this.currency_amount = this.currency.amount;
      }
    },
    relations() {
      var sum = 0;
      this.relations.map(function (item) {
        sum = parseFloat(sum) + parseFloat(item.price.price);
      });
      this.sumRelations = sum;
    },
  },
  beforeMount() {
    if (this.accountProp) {
      this.account = this.accountProp;
    }
    if (this.$route.params.id != null) {
      this.show();
      this.id = this.$route.params.id;
      this.render = false;
    } else {
      this.render = true;
      this.defaultCurrency = this.getDefault("defaultCurrency");
    }
    if (this.$store.state.currency && !this.id) {
      this.currency = this.$store.state.currency;
      this.currency_amount = this.$store.state.currency.amount;
    }
  },
  mounted() {
    this.setDate();
    this.taxStatusItems = [
      {
        value: "add",
        text: this.$t("add-tax-to-price"),
      },
      {
        value: "notAdd",
        text: this.$t("calculate-tax-on-price"),
      },
    ];
    this.taxStatus = "notAdd";
  },
  methods: {
    changeCurrency() {
      this.bankRemain = 0;
      this.currency_amount = this.currency.amount;
      this.options = {
        locale: "en-US",
        prefix: "",
        suffix: this.currency.name,
        precision: this.currency.decimal,
      };
    },
    SetValueMaxPrice() {
      if (this.render && this.type === "pay") {
        this.amount = 0;
      }
    },
    bankRemainCheck() {
      if (this.render && this.bank && this.type === "pay") {
        if (this.$route.params.id != null) {
          this.bankRemain =
            parseFloat(this.bank.remain.currency_remain) - parseFloat(this.amount);
        } else {
          this.bankRemain =
            parseFloat(this.bank.remain.currency_remain) - parseFloat(this.amount);
        }
        this.remianBankColor = "grey";
        if (this.bankRemain > 0) {
          this.remianBankColor = "green";
        }
        if (this.bankRemain < 0) {
          this.remianBankColor = "pink";
        }
        if (
          this.bank &&
          parseFloat(this.amount) > parseFloat(this.bank.remain.currency_remain)
        ) {
          this.amount = 0;
          this.bankRemain =
            parseFloat(this.bank.remain.currency_remain) - parseFloat(this.amount);
          this.snakbarText = this.$t("alert-pay-amount-bigger-than-bank-remain");
          this.snakbarColor = "pink";
          this.snakbar = true;
        }
      }
    },
    SetValueMaxDiscount() {
      this.discount = this.price;
    },
    create() {
      if (!this.$refs.form.validate()) return false;
      var app = this;
      app.isLoading = true;
      app.$axios
        .post(this.$endpoint + "transfer", this.$data)
        .then((res) => {
          res = res.data;
          app.snakbarText = app.$t("registered");
          app.snakbarColor = "green";
          app.snakbar = true;
          if (app.$refs.refFilePicker !== undefined && app.attachments) {
            app.$refs.refFilePicker.create(res.data.id);
          }
          app.$emit("close", app.$route);

          app.goTo("transferList");
        })
        .catch((err) => {
          if (err.response && err.response.status === 409) {
            this.$store.commit("SET_PLAN_DIALOG", true);
          }
          this.serverErrorHandeler(err);
        })
        .finally(() => {
          this.isLoading = false;
          // this.clearForm()
        });
    },
    update() {
      var app = this;
      app.isLoading = true;
      app.$axios
        .put(this.$endpoint + "transfer/" + this.$route.params.id, this.$data)
        .then((res) => {
          res = res.data;
          if (app.$refs.refFilePicker !== undefined) {
            app.$refs.refFilePicker.create(res.data.id);
          }
          app.$emit("close", app.$route);
          app.goTo("transferList");
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.isLoading = false));
    },
    show() {
      var app = this;
      app.clearForm();
      app.loading = true;
      app.$axios
        .get(this.$endpoint + "transfer/" + this.$route.params.id + "/edit", {
          params: {
            id: this.$route.params.id,
          },
        })
        .then((res) => {
          app.$nextTick(() => {
            res = res.data;
            app.currency = res.currency;
            app.currency_amount = res.currency_amount;
            app.type = res.type;
            app.id = res.id;
            app.attachments = res.attachments;
            app.amount = res.amount;
            app.payer = res.payer;
            app.payerGiven = res.payer_given;
            app.receiver = res.receiver;
            app.receiverGiven = res.receiver_given;
            app.date = res.date;
            app.content = res.content;
            if (res.tags) {
              app.tag = res.tags;
              app.more = true;
            }
            app.render = true;
          });
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    clearForm() {
      this.more = true;
      this.defaultCurrency = null;
      this.attachments = null;
      this.accountGiven = null;
      this.bankGiven = null;
      this.id = null;
      this.extra = false;
      this.relations = null;
      this.snakbar = null;
      this.snakbarText = null;
      this.snakbarColor = null;
      this.sumRelations = null;
      this.currency_amount = 1;
      this.tag = null;
      this.remianBankColor = "blue";
      this.remianColor = "blue";
      this.customerId = null;
      this.bankRemain = null;
      this.taxStatus = null;
      this.clearing = null;
      this.snackbarShow = null;
      this.amount = 0;
      this.editPrice = 0;
      this.type = null;
      this.NetPrice = 0;
      this.taxAmount = 0;
      this.tax = 9;
      this.discount = 0;
      this.taxFeilds = null;
      this.discountFeilds = null;
      this.account = null;
      this.detail = null;
      this.bank = null;
      this.remianLable = null;
      this.description = null;
      this.date = null;
    },
  },
};
</script>

<style scoped>
.col {
  height: 55px !important;
}
</style>
